import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { NotificationService, Notification } from '@core/services/notification.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-toast',
    imports: [CommonModule],
    template: `
    <div class="toast-container" role="status" aria-live="polite" aria-atomic="true">
      <div
        *ngFor="let notification of notifications"
        [class]="'toast toast-' + notification.type"
        [@slideIn]
      >
        <div class="toast-content">
          <div class="toast-icon">
            <span *ngIf="notification.type === 'success'">✓</span>
            <span *ngIf="notification.type === 'error'">✕</span>
            <span *ngIf="notification.type === 'warning'">⚠</span>
            <span *ngIf="notification.type === 'info'">ℹ</span>
          </div>
          <div class="toast-message">
            <div class="toast-title" *ngIf="notification.title">{{ notification.title }}</div>
            <div class="toast-text">{{ notification.message }}</div>
          </div>
          <button class="toast-close" (click)="removeNotification(notification.id)">×</button>
        </div>
        <div class="toast-progress" *ngIf="notification.duration">
          <div
            class="toast-progress-bar"
            [style.animation-duration.ms]="notification.duration"
          ></div>
        </div>
      </div>
    </div>
  `,
    styles: [`
    .toast-container {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 12px;
      max-width: 400px;
      pointer-events: none;
    }

    .toast {
      background: var(--bg);
      border-radius: var(--radius-md, 8px);
      box-shadow: var(--shadow-md, 0 4px 12px rgba(0, 0, 0, 0.15));
      overflow: hidden;
      pointer-events: auto;
      animation: slideInRight 0.3s ease-out;
      border-left: 4px solid;
    }

    .toast-success {
      border-left-color: var(--color-success, #10b981);
    }

    .toast-error {
      border-left-color: var(--color-error, #ef4444);
    }

    .toast-warning {
      border-left-color: var(--color-warning, #f59e0b);
    }

    .toast-info {
      border-left-color: var(--color-info, #3b82f6);
    }

    .toast-content {
      display: flex;
      align-items: flex-start;
      padding: 16px;
      gap: 12px;
    }

    .toast-icon {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      font-weight: bold;
      font-size: 14px;
    }

    .toast-success .toast-icon {
      background: var(--color-success, #10b981);
      color: white;
    }

    .toast-error .toast-icon {
      background: var(--color-error, #ef4444);
      color: white;
    }

    .toast-warning .toast-icon {
      background: var(--color-warning, #f59e0b);
      color: white;
    }

    .toast-info .toast-icon {
      background: var(--color-info, #3b82f6);
      color: white;
    }

    .toast-message {
      flex: 1;
      min-width: 0;
    }

    .toast-title {
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 4px;
      color: var(--text);
    }

    .toast-text {
      font-size: 14px;
      line-height: 1.5;
      color: var(--text);
      word-wrap: break-word;
    }

    .toast-error .toast-title,
    .toast-error .toast-text {
      color: var(--color-error, #ef4444);
    }

    .toast-close {
      flex-shrink: 0;
      background: none;
      border: none;
      font-size: 24px;
      line-height: 1;
      color: var(--muted);
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.2s;
    }

    .toast-close:hover {
      color: var(--text);
    }

    .toast-progress {
      height: 3px;
      background: rgba(0, 0, 0, 0.1);
      overflow: hidden;
    }

    .toast-progress-bar {
      height: 100%;
      background: currentColor;
      animation: progress linear forwards;
    }

    .toast-success .toast-progress-bar {
      background: var(--color-success, #10b981);
    }

    .toast-error .toast-progress-bar {
      background: var(--color-error, #ef4444);
    }

    .toast-warning .toast-progress-bar {
      background: var(--color-warning, #f59e0b);
    }

    .toast-info .toast-progress-bar {
      background: var(--color-info, #3b82f6);
    }

    @keyframes slideInRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes progress {
      from {
        width: 100%;
      }
      to {
        width: 0%;
      }
    }
  `],
    animations: [
        trigger('slideIn', [
            transition(':enter', [
                style({ transform: 'translateX(100%)', opacity: 0 }),
                animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 }))
            ])
        ])
    ]
})
export class ToastComponent implements OnInit, OnDestroy {
  notifications: Notification[] = [];
  private subscription?: Subscription;

  constructor(private notificationService: NotificationService) {}

  ngOnInit(): void {
    this.subscription = this.notificationService.notifications$.subscribe(
      (notification) => {
        this.addNotification(notification);
      }
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private addNotification(notification: Notification): void {
    if (notification.collapseGroup) {
      this.notifications = this.notifications.filter(
        (n) => n.collapseGroup !== notification.collapseGroup
      );
    }

    this.notifications.push(notification);

    if (notification.duration && notification.duration > 0) {
      setTimeout(() => {
        this.removeNotification(notification.id);
      }, notification.duration);
    }
  }

  removeNotification(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
  }
}
